import { LineOrientation } from '../models/interfaces';
import { TableData, TableOverlayLine } from '../table-overlay/table-overlay';
import { signal } from '@angular/core';
import {
  BuildSimpleExtractedFieldDto,
  BuildSimpleExtractedTableRowDto,
  BuildSimplePageLocation,
} from '../models/build-simple-model';
import { compareBy, mapValues, maxBy, minBy } from '@ps-gui/shared-utils';
import { sortBy } from '@vydaps/gui-commons/arrays';
import { WordRect } from '@ps-gui/claims-terminal-shared';
import {
  getLocatedMemberEntries,
  getLocatedMembers,
  getRowLowerOffset,
} from './build-simple-row-access';

export const extractTableLinesFromBuildSimpleRows = (
  rows: BuildSimpleExtractedTableRowDto[],
): { extractedLines: TableData['lines']; columnHeaders: string[] } => {
  const fieldsByColumnKey = getFieldsByColumnKey(rows);
  const tableBoundary = getBoundingRect(rows);

  const verticalRightOffsetsByColumnKey = mapValues(
    fieldsByColumnKey,
    (_, fields) => maxBy(fields, (field) => field.location.right)!.location.right,
  );

  const sortedColumnKeys = sortBy(
    Object.keys(fieldsByColumnKey),
    (columnKey) => verticalRightOffsetsByColumnKey[columnKey],
  );

  const verticalInBetweenLines = Object.values(verticalRightOffsetsByColumnKey)
    .map((rightOffset) => ({
      offset: signal(rightOffset),
      orientation: LineOrientation.VERTICAL,
    }))
    .toSorted(lineOffsetComparator)
    .slice(0, -1);

  const lines = {
    vertical: signal(getVerticalLines(tableBoundary, verticalInBetweenLines)),
    horizontal: signal(getHorizontalLines(tableBoundary, getHorizontalInBetweenLines(rows))),
  };

  return {
    extractedLines: lines,
    columnHeaders: sortedColumnKeys,
  };
};

const getFieldsByColumnKey = (
  rows: BuildSimpleExtractedTableRowDto[],
): Record<string, BuildSimpleExtractedFieldDto[]> => {
  const tableColumns: Record<string, BuildSimpleExtractedFieldDto[]> = {};
  for (const row of rows) {
    for (const [key, field] of getLocatedMemberEntries(row)) {
      if (!tableColumns[key]) tableColumns[key] = [];
      tableColumns[key].push(field);
    }
  }
  return tableColumns;
};

const getHorizontalInBetweenLines = (rows: BuildSimpleExtractedTableRowDto[]): TableOverlayLine[] =>
  rows
    .map((row) => getRowLowerOffset(row))
    .filter((lowerOffset) => lowerOffset !== undefined)
    .map((lowerOffset) => ({
      offset: signal(lowerOffset),
      orientation: LineOrientation.HORIZONTAL,
    }))
    .toSorted(lineOffsetComparator)
    .slice(0, -1);

const getVerticalLines = (tableBoundary: WordRect, verticalInBetweenLines: TableOverlayLine[]) => [
  {
    offset: signal(tableBoundary.x[0]),
    orientation: LineOrientation.VERTICAL,
    isBoundary: true,
  },
  ...verticalInBetweenLines,
  {
    offset: signal(tableBoundary.x[1]),
    orientation: LineOrientation.VERTICAL,
    isBoundary: true,
  },
];

const getHorizontalLines = (
  tableBoundary: WordRect,
  horizontalInBetweenLines: TableOverlayLine[],
) => [
  {
    offset: signal(tableBoundary.y[0]),
    orientation: LineOrientation.HORIZONTAL,
    isBoundary: true,
  },
  ...horizontalInBetweenLines,
  {
    offset: signal(tableBoundary.y[1]),
    orientation: LineOrientation.HORIZONTAL,
    isBoundary: true,
  },
];

const getBoundingRect = (rows: BuildSimpleExtractedTableRowDto[]) =>
  getBoundingRectForLocations(getTableCellLocations(rows));

const getTableCellLocations = (rows: BuildSimpleExtractedTableRowDto[]) =>
  rows.flatMap((row) => getLocatedMembers(row).map((field) => field.location));

const getBoundingRectForLocations = (locations: BuildSimplePageLocation[]): WordRect => {
  const minX = minBy(locations, (location) => location.left)?.left ?? 0;
  const maxX = maxBy(locations, (location) => location.right)?.right ?? 0;
  const minY = minBy(locations, (location) => location.upper)?.upper ?? 0;
  const maxY = maxBy(locations, (location) => location.lower)?.lower ?? 0;

  return { x: [minX, maxX], y: [minY, maxY] } as const;
};

const lineOffsetComparator = compareBy<TableOverlayLine>((line) => line.offset());
